import React, { Fragment, useEffect, useState } from 'react'
import {
  Grid,
  TextField,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  
  FormControl,
  InputAdornment,
  IconButton,
  Tooltip
} from '@mui/material'
import { makeStyles } from '@mui/styles'
import { Search, Refresh } from '@mui/icons-material'
import { RestockDetail, RestockOrderCreate, Stock } from './Entities/Stock'
import { useApi } from '../../hooks/useApi'
import { ApiResponse } from '../../typings/ApiResponse'
import { getStockReserve, postRestockOrder } from './api'
import { Loading } from '../../components/UI'
import { Status } from '../../hooks/useFetchState'
import { usePagination } from '../../hooks/usePagination'
import { Filter } from '../../components/_WMS/FilterComponent'
import { Pagination } from '../../components/Pagination'
import { WarehouseDDL } from '../../components/Almacen/UI/WarehouseDDL'
import { ProductOriginsDDL } from '../../components/Producto/ProductOrigins/ProductOriginsDDL'
import { Modal } from '../../components/_EOS/Modal'
import { useModal } from '../../hooks/useModal'
import { SuppliersDDL } from '../../components/Supplier/SuppliersDDL'
import { SupplierOriginDDL } from '../../components/SupplierOrigin/SupplierOriginDDL'
import { eTypeStorage } from '../../enums/eTypeStorage'
import { useProductOrigins, useSuppliers, useWarehouses } from '../../hooks'
import { useSuppliersOrigin } from '../../hooks/useSuppliersOrigin'
import { eTypeOperation } from '../../enums/eTypeOperation'
import { Warehouse } from '../../interfaces'

interface CreateRestockOrderModalProps {
  handleSnackbar: (message: string, success: boolean) => void
  handleCloseModal: () => void
  requestRestockOrder: (
    filters?: Filter[],
    page?: number,
    pageSize?: number
  ) => void
}

export const CreateRestockOrderModal: React.FC<
  CreateRestockOrderModalProps
> = ({ handleSnackbar, handleCloseModal, requestRestockOrder }) => {
  const classes = useStyles()
  const [errors, setErrors] = React.useState<Record<string, boolean>>({})

  const [loading, setLoading] = useState<boolean>(false)
  const { state, callApi } = useApi<ApiResponse<Stock>>()
  const pagination = usePagination(10)
  const [selectedFilters, setSelectedFilters] = React.useState<Filter[]>()
  // Lista inicial de elementos
  const [items, setItems] = useState<Stock[]>([])
  const [selectedWarehouse, setSelectedWarehouse] = useState<number>(-1)
  const [selectedProductOrigins, setSelectedProductOrigins] =
    useState<number>(0)
  const [selectedSupplier, setSelectedSupplier] = useState<number>(0)
  const [selectedSupplierOrigin, setSelectedSupplierOrigin] =
    useState<number>(0)
  // Estado para las cantidades ingresadas
  const [quantities, setQuantities] = useState<{ [key: string]: number }>({})
  const [enableSearchSku, setEnableSearchSku] = useState<boolean>(false)

  // Lista de elementos agregados
  const [addedItems, setAddedItems] = useState<Stock[]>([])
  const [filterSku, setFilterSku] = useState<string>('')

  const [allowedWarehouseIds, setAllowedWarehouseIds] = useState<number[]>([])
  const [allowedSupplierIds, setAllowedSupplierIds] = useState<number[]>([])
  const [allowedSupplierOriginIds, setAllowedSupplierOriginIds] = useState<
    number[]
  >([])

  const handlePaginationChange = (page: number, pageSize: number) => {
    // If the pageSize is different, we should set page 0
    const auxPage = pagination.pageSize !== pageSize ? 0 : page

    setItems([])

    requestGetStockReserve(selectedFilters, auxPage, pageSize)

    pagination.setPagination(auxPage, pageSize)
  }

  const handleSetWarehouse = (id: number) => {
    setSelectedWarehouse(id)
    setItems([])
    setEnableSearchSku(false)
  }

  const handleSetProductOrigin = (id: number) => {
    setSelectedProductOrigins(id)
    setSelectedWarehouse(-1)
    setItems([])
    setEnableSearchSku(false)

    const selected = productOrigins?.find((po) => po.productOriginId === id)

    if (selected) {
      // validamos que tenga configurado el tipo de operacion externo
      const typeOp = selected.typeOperation?.find(
        (wo: any) => wo.typeOperationId === eTypeOperation.Externo
      )

      if (typeOp) {
        const warehouseAlloweds: number[] =
          typeOp?.warehouses?.map((w: any) => w.warehouseId) || []

        if (warehouseAlloweds.length === 0) {
          handleSnackbar(
            'La marca seleccionada no tiene bodegas configuradas para este flujo',
            false
          )
        } else {
          // validamops si hay bodegas con zonas externas
          const warehousesExternalAllowed: number[] =
            warehouses
              ?.filter(
                (item) =>
                  item.withExternalStorage &&
                  warehouseAlloweds.includes(item.id)
              )
              .map((w: Warehouse) => w.id) || []

          if (warehousesExternalAllowed.length === 0) {
            handleSnackbar(
              'La marca seleccionada no tiene bodegas con almacenamiento externo configurado para este flujo',
              false
            )
          } else {
            setAllowedWarehouseIds(warehousesExternalAllowed)

            // si hay solo una bodega, queda seleccionada
            if (warehousesExternalAllowed.length === 1) {
              handleSetWarehouse(warehousesExternalAllowed[0])
            }
          }
        }
      } else {
        handleSnackbar(
          'No hay tipo de operación Externa configurada, para marca seleccionada',
          false
        )
      }
    }
  }

  const requestGetStockReserve = (
    filters?: Filter[],
    page?: number,
    pageSize?: number
  ) => {
    setLoading(true)
    callApi(getStockReserve(filters, page, pageSize))
      .then((data) => {
        setItems(data.items as Stock[])
        setLoading(false)
      })
      .catch((error) => {
        handleSnackbar(error, false)
      })
    setLoading(false)
  }

  const handleSubmit = (pSupplierId?: number, pSuppOriginId?: number) => {
    setLoading(true)

    const dto: RestockOrderCreate = {
      originId: selectedProductOrigins,
      warehouseId: selectedWarehouse,
      supplierId: pSupplierId ? pSupplierId || 0 : selectedSupplier,
      supplierOriginId: pSuppOriginId
        ? pSuppOriginId || 0
        : selectedSupplierOrigin,
      details: addedItems.map(
        (i): RestockDetail => ({
          skuId: i.skuId,
          positionId: i.positionId,
          quantity: i.quantity
        })
      )
    }

    callApi(postRestockOrder(dto))
      .then(() => {
        handleSnackbar('Creación realizada exitosamente', true)
        requestRestockOrder()
        handleCloseModal()
      })
      .catch((error) => {
        requestGetStockReserve()
        handleSnackbar(error, false)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  // Agregar elemento a la lista inferior y descuenta la cantidad
  const handleAddItem = (item: Stock) => {
    const quantityToAdd = quantities[`${item.positionId}_${item.skuId}` || 0]

    if (quantityToAdd > 0 && quantityToAdd <= item.quantity) {
      // Luego, agregar el producto a la lista de addedItems
      setAddedItems((prev) => {
        const existingItem = prev.find(
          (i) =>
            `${i.positionId}_${i.skuId}` === `${item.positionId}_${item.skuId}`
        )

        if (existingItem) {
          // Si el elemento ya existe, sumar la cantidad
          return prev.map((i) =>
            `${i.positionId}_${i.skuId}` === `${item.positionId}_${item.skuId}`
              ? { ...i, quantity: i.quantity + quantityToAdd }
              : i
          )
        } else {
          // Si no existe, agregarlo como nuevo
          return [...prev, { ...item, quantity: quantityToAdd }]
        }
      })
      // Resetear el campo de cantidad
      setQuantities((prev) => ({
        ...prev,
        [`${item.positionId}_${item.skuId}`]: 0
      }))
    } else {
      // Si la cantidad es mayor a la disponible, puedes mostrar un mensaje de error
      handleSnackbar('Cantidad excede la cantidad disponible', false)
    }
  }

  const handleRemoveItem = (item: Stock) => {
    // Eliminar el item de los productos agregados
    setAddedItems((prev) =>
      prev.filter(
        (i) =>
          `${i.positionId}_${i.skuId}` !== `${item.positionId}_${item.skuId}`
      )
    )
    // llamar nuevamente a la API
    handleSearchProduct()
  }

  const setFiltersRequired = (prevFilter?: Filter[]) => {
    setSelectedFilters([
      {
        key: 'warehouseId',
        value: selectedWarehouse,
        label: 'bodega',
        displayValue: 'bodega'
      },
      {
        key: 'productOriginId',
        value: selectedProductOrigins,
        label: '',
        displayValue: ''
      },
      ...(prevFilter || [])
    ])
  }

  const handleSearchProduct = () => {
    pagination.setPagination(0, pagination.pageSize)

    setFiltersRequired()
    setEnableSearchSku(true)
  }

  useEffect(() => {
    if (selectedFilters && selectedFilters.length > 0) {
      requestGetStockReserve(
        selectedFilters,
        pagination.page,
        pagination.pageSize
      )
    }
  }, [selectedFilters])

  // Manejar cambios en las cantidades
  const handleQuantityChange = (id: string, value: string) => {
    const quantity = parseInt(value, 10)

    // Verificar que la cantidad sea solo positiva
    if (quantity < 0) {
      return
    }
    // Aquí se verifica la cantidad restante
    const remainingQuantity =
      (items.find((item) => `${item.positionId}_${item.skuId}` === id)
        ?.quantity || 0) -
      (addedItems.find(
        (addedItem) => `${addedItem.positionId}_${addedItem.skuId}` === id
      )?.quantity || 0)

    const isError = quantity > remainingQuantity

    setErrors((prevErrors) => ({
      ...prevErrors,
      [id]: isError
    }))

    setQuantities((prev) => ({
      ...prev,
      [id]: isNaN(quantity) ? 0 : quantity
    }))
  }

  const handleSearchSkuChange = (sku: string) => {
    setFilterSku(sku)
  }

  // si no estan los filtros obligatorios, el boton esta desactivado
  const validateButtonLoad = () => {
    if (selectedProductOrigins <= 0 || selectedWarehouse <= 0) return true
    else return false
  }

  // si hay items agregados, no puede buscar
  const validateDisableSearch = () => {
    if (addedItems && addedItems.length > 0) return true
    else return false
  }

  const validateDisableWarehouse = () => {
    return (
      allowedWarehouseIds.length === 0 ||
      allowedWarehouseIds.length === 1 ||
      selectedProductOrigins === 0 ||
      (addedItems && addedItems.length > 0)
    )
  }

  // Limpia la busqueda por SKU
  const cleanSearchSku = () => {
    setFilterSku('')
    setFiltersRequired()
  }

  // Permite buscar por SKU
  const searchSku = () => {
    pagination.setPagination(0, pagination.pageSize)
    setFiltersRequired([
      {
        key: 'stockKeepingUnit',
        value: filterSku,
        label: '',
        displayValue: ''
      }
    ])
  }

  const {
    suppliersState: { suppliers },
    getSuppliers
  } = useSuppliers()

  useEffect(() => {
    if (suppliers?.length === 0) {
      getSuppliers()
    }
  }, [])

  const {
    suppliersOriginState: { suppliersOrigin },
    getSuppliersOrigin
  } = useSuppliersOrigin()

  useEffect(() => {
    if (suppliersOrigin?.length === 0) {
      getSuppliersOrigin()
    }
  }, [])

  const {
    productOriginsState: { productOrigins },
    getProductOrigins
  } = useProductOrigins()

  useEffect(() => {
    if (suppliersOrigin?.length === 0) {
      getProductOrigins()
    }
  }, [])

  const {
    warehousesState: { warehouses },
    getWarehouses
  } = useWarehouses()

  useEffect(() => {
    if (warehouses?.length === 0) {
      getWarehouses()
    }
  }, [])

  const handleSelectedSuppliers = (
    warehouseId: number,
    productOriginId: number
  ) => {
    const suppliersAllowedAux =
      suppliers
        ?.filter(
          (s) =>
            s.warehouseIds.includes(warehouseId) &&
            s.productOriginId === productOriginId
        )
        .map((m) => m.supplierId) || []

    if (suppliersAllowedAux.length === 0) {
      handleSnackbar(
        'No hay proveedor configurado, para Marca seleccionada',
        false
      )
    } else {
      setAllowedSupplierIds(suppliersAllowedAux)

      // si es solo uno, seteamos el valor, si no, abrimos el modal
      if (suppliersAllowedAux.length === 1) {
        handleSetSupplier(suppliersAllowedAux[0])
      } else {
        confirmCreateModalState.openModal('sm')
      }
    }
  }

  const handleSetSupplier = (id: number) => {
    setSelectedSupplierOrigin(0)
    setSelectedSupplier(id)

    const suppOriginAux =
      suppliersOrigin
        ?.filter((item) => item.active && item.supplierId === id)
        .map((m) => m.id) || []
    if (suppOriginAux.length === 0) {
      handleSnackbar(
        'No hay Origen de proveedor configurado, para proveedor seleccionada',
        false
      )
    } else {
      setAllowedSupplierOriginIds(suppOriginAux)

      // Si hay mas de uno, se abre el modal, si es solo uno, se realiza la creación sin validación de proveedores
      if (suppOriginAux.length === 1) {
        setSelectedSupplier(id)
        setSelectedSupplierOrigin(suppOriginAux[0])

        handleSubmit(id, suppOriginAux[0])
      } else {
        confirmCreateModalState.openModal('sm')
      }
    }
  }

  const handleSetSupplierOrigin = (id: number) => {
    setSelectedSupplierOrigin(id)
  }

  const isDisableSupplier = () => {
    return allowedSupplierIds.length === 1
  }

  const isDisableSupplierOrigin = () => {
    return selectedSupplier <= 0 || allowedSupplierOriginIds.length === 1
  }

  const confirmCreateModalState = useModal('Crear Orden')

  if (state.status === Status.Loading || loading) {
    return (
      <div>
        <Typography>
          <Loading />
        </Typography>
      </div>
    )
  } else {
    return (
      <Fragment>
        <Grid
          container
          spacing={2} // Espaciado entre los elementos
          alignItems='center'
          justifyContent='center'
          style={{
            margin: 'auto',
            padding: '4px',
            border: '1px solid #ccc',
            borderRadius: '8px'
          }}
        >
          <Grid
            item
            xs={4}
            justifyContent='center'
            alignItems='center'
            alignContent='center'
            style={{ textAlign: 'center' }}
          >
            <ProductOriginsDDL
              onChange={(e: any) =>
                handleSetProductOrigin(e.target.value as number)
              }
              value={selectedProductOrigins}
              required={true}
              type='outlined'
              fullWidth
              disabled={validateDisableSearch()}
              size='small'
              typeStorages={[eTypeStorage.Externo, eTypeStorage.Mixto]}
            />
          </Grid>

          <Grid
            item
            xs={4}
            justifyContent='center'
            alignItems='center'
            alignContent='center'
            style={{ textAlign: 'center' }}
          >
            <WarehouseDDL
              onChange={(e: any) =>
                handleSetWarehouse(e.target.value as number)
              }
              value={selectedWarehouse}
              required={true}
              type='outlined'
              fullWidth
              size='small'
              disabled={validateDisableWarehouse()}
              onlyExternalStorage={true}
              allowedIds={allowedWarehouseIds}
            />
          </Grid>
          <Grid
            item
            xs={4}
            justifyContent='center'
            alignItems='center'
            alignContent='center'
            style={{ textAlign: 'center' }}
          >
            <Button
              variant='contained'
              color='primary'
              size='small'
              disabled={validateButtonLoad() || validateDisableSearch()}
              onClick={handleSearchProduct}
            >
              Buscar
            </Button>
          </Grid>
        </Grid>
        <br />
        <Grid item xs={12} className={classes.grid}>
          <Grid
            item
            xs={12}
            justifyContent='center'
            alignItems='center'
            alignContent='center'
            style={{ textAlign: 'center' }}
          >
            {enableSearchSku && (
              <FormControl>
                <TextField
                  size='small'
                  id='sku'
                  name='sku'
                  label='SKU'
                  value={filterSku}
                  onChange={(e: any) => handleSearchSkuChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      searchSku()
                    }
                  }}
                  variant='outlined'
                  placeholder='SKU a buscar...'
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position='end'>
                        <IconButton
                          onClick={searchSku}
                          style={{
                            backgroundColor: '#1976d2',
                            color: 'white',
                            borderRadius: '4px'
                          }}
                          size='small'
                        >
                          <Search />
                        </IconButton>
                        <IconButton
                          onClick={cleanSearchSku}
                          style={{
                            backgroundColor: '#e0e0e0',
                            color: '#616161',
                            borderRadius: '4px'
                          }}
                          size='small'
                        >
                          <Refresh />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </FormControl>
            )}
          </Grid>
          <Typography className={classes.title}>Disponibles</Typography>
          <Paper className={classes.paper}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow className={classes.tableRow}>
                    <TableCell className={classes.tableTitle}>SKU</TableCell>
                    <TableCell className={classes.tableTitle}>EAN13</TableCell>
                    <TableCell className={classes.tableTitle}>
                      Ubicación
                    </TableCell>
                    <TableCell className={classes.tableTitle}>
                      Nombre Producto
                    </TableCell>
                    <TableCell className={classes.tableTitle}>
                      Un. Disponible
                    </TableCell>
                    <TableCell className={classes.tableTitle}>
                      Un. Reabastecer
                    </TableCell>
                    <TableCell className={classes.tableTitle}>Acción</TableCell>
                  </TableRow>
                </TableHead>
                {items && items.length > 0 ? (
                  <TableBody>
                    {items.map((item) => {
                      // calculamos los items que tienen diferencia
                      const remainingQuantityAux =
                        item.quantity -
                        (addedItems.find(
                          (addedItem) =>
                            `${addedItem.positionId}_${addedItem.skuId}` ===
                            `${item.positionId}_${item.skuId}`
                        )?.quantity || 0)

                      // Determinar si la fila debe estar deshabilitada
                      const isDisabled = remainingQuantityAux <= 0

                      return (
                        <TableRow
                          key={`${item.positionId}_${item.skuId}`}
                          className={`${classes.tableRow} ${
                            isDisabled ? classes.tableRowDisable : ''
                          }`}
                        >
                          <TableCell className={classes.tableCell}>
                            {item.sku}
                          </TableCell>
                          <TableCell className={classes.tableCell}>
                            {item.ean13}
                          </TableCell>
                          <TableCell className={classes.tableCell}>
                            {item.positionCode}
                          </TableCell>
                          <TableCell className={classes.tableCell}>
                            {item.name}
                          </TableCell>
                          <TableCell className={classes.tableCell}>
                            {remainingQuantityAux}
                          </TableCell>
                          <TableCell className={classes.tableCell}>
                            <FormControl>
                              <TextField
                                type='number'
                                id='qty'
                                name='qty'
                                value={
                                  quantities[
                                    `${item.positionId}_${item.skuId}`
                                  ] || ''
                                }
                                onChange={(e) =>
                                  handleQuantityChange(
                                    `${item.positionId}_${item.skuId}`,
                                    e.target.value
                                  )
                                }
                                variant='outlined'
                                placeholder='Ingrese Qty...'
                                size='small'
                                disabled={isDisabled}
                                error={
                                  !!errors[`${item.positionId}_${item.skuId}`]
                                }
                                helperText={
                                  errors[`${item.positionId}_${item.skuId}`]
                                    ? 'Cantidad excede la disponible'
                                    : ''
                                }
                                InputProps={{
                                  style: {
                                    fontSize: '12px',
                                    padding: '3px'
                                  }
                                }}
                              />
                            </FormControl>
                          </TableCell>
                          <TableCell className={classes.tableCell}>
                            <Button
                              variant='contained'
                              size='small'
                              onClick={() => handleAddItem(item)}
                              className={`${classes.button} ${classes.addButton}`}
                              disabled={
                                !quantities[
                                  `${item.positionId}_${item.skuId}`
                                ] || errors[`${item.positionId}_${item.skuId}`]
                              }
                            >
                              Agregar
                            </Button>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                ) : (
                  <div>
                    <Typography className={classes.empty}>
                      {'No existe información'}
                    </Typography>
                  </div>
                )}
              </Table>
            </TableContainer>
          </Paper>

          <Pagination
            pagination={{
              totalItem: state.data?.totalItems || 0,
              page: pagination.page,
              pageSize: pagination.pageSize
            }}
            onPaginationChange={handlePaginationChange}
          />
        </Grid>
        <br />
        <Grid item xs={12} className={classes.grid}>
          <Grid
            item
            xs={12}
            justifyContent='center'
            alignItems='center'
            alignContent='center'
            style={{ textAlign: 'center' }}
          >
            {addedItems && addedItems.length > 0 && (
              <Tooltip
                title='Crear Order de Reabastecimiento, con los productos agregados'
                placement='top-end'
              >
                <Button
                  className='btn-upload'
                  color='primary'
                  variant='contained'
                  component='span'
                  disabled={validateButtonLoad()}
                  onClick={() => {
                    handleSelectedSuppliers(
                      selectedWarehouse,
                      selectedProductOrigins
                    )
                  }}
                >
                  Crear Orden
                </Button>
              </Tooltip>
            )}
          </Grid>
          <Typography variant='h6' className={classes.title}>
            Productos Agregados
          </Typography>
          <Paper className={classes.paper}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow className={classes.tableRow}>
                    <TableCell className={classes.tableTitle}>SKU</TableCell>
                    <TableCell className={classes.tableTitle}>EAN13</TableCell>
                    <TableCell className={classes.tableTitle}>
                      Ubicación
                    </TableCell>
                    <TableCell className={classes.tableTitle}>
                      Nombre Producto
                    </TableCell>
                    <TableCell className={classes.tableTitle}>
                      Un. Reabastecer
                    </TableCell>
                    <TableCell className={classes.tableTitle}>Acción</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {addedItems.map((addedItem, index) => (
                    <TableRow key={index} className={classes.tableRow}>
                      <TableCell className={classes.tableCell}>
                        {addedItem.sku}
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        {addedItem.ean13}
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        {addedItem.positionCode}
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        {addedItem.name}
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        {addedItem.quantity}
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        <Button
                          variant='contained'
                          size='small'
                          className={`${classes.button} ${classes.removeButton}`}
                          onClick={() => handleRemoveItem(addedItem)}
                        >
                          Quitar
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        <Modal
          open={confirmCreateModalState.isOpen}
          title={confirmCreateModalState.title}
          maxWidth={confirmCreateModalState.size}
          onClose={confirmCreateModalState.closeModal}
          successButton={{
            action: () => handleSubmit(),
            title: 'Aceptar',
            disabled: selectedSupplier <= 0 || selectedSupplierOrigin <= 0
          }}
          cancelButton={{
            action: confirmCreateModalState.closeModal,
            title: 'Salir'
          }}
        >
          <Fragment>
            <Typography align='center' variant='h6' style={{ color: 'black' }}>
              ¿Está seguro que desea Crear la orden con los ítems seleccionados?
            </Typography>
            <br />
            <Grid
              container
              spacing={2}
              justifyContent='space-around'
              alignItems='flex-start'
              style={{ textAlign: 'center', marginBottom: '5px' }}
            >
              <Grid item xs={12}>
                <SuppliersDDL
                  onChange={(e: any) =>
                    handleSetSupplier(e.target.value as number)
                  }
                  value={selectedSupplier}
                  required={true}
                  type='outlined'
                  fullWidth={false}
                  size='small'
                  allowedWarehouseIds={[selectedWarehouse]}
                  productOriginId={selectedProductOrigins}
                  disabled={isDisableSupplier()}
                />
              </Grid>

              <Grid item xs={12}>
                <SupplierOriginDDL
                  onChange={(e: any) =>
                    handleSetSupplierOrigin(e.target.value as number)
                  }
                  value={selectedSupplierOrigin}
                  required={true}
                  type='outlined'
                  fullWidth={false}
                  size='small'
                  disabled={isDisableSupplierOrigin()}
                  supplierId={selectedSupplier}
                />
              </Grid>
            </Grid>
          </Fragment>
        </Modal>
      </Fragment>
    )
  }
}

const useStyles = makeStyles({
  container: {
    justifyContent: 'flex-end'
  },
  grid: {
    margin: 'auto',
    padding: '10px',
    border: '1px solid #ccc',
    borderRadius: '8px'
  },
  paper: {
    maxHeight: 250,
    overflow: 'auto'
  },
  tableRow: {
    height: 25
  },
  tableRowDisable: {
    backgroundColor: '#f5f5f5',
    opacity: 0.6,
    pointerEvents: 'none'
  },
  tableCell: {
    padding: '4px',
    fontSize: '12px'
  },
  tableTitle: {
    padding: '6px',
    fontSize: '12px',
    fontWeight: 'bold'
  },
  title: {
    fontSize: '16px',
    fontWeight: 'bold'
  },
  button: {
    fontSize: '10px',
    minHeight: '20px',
    padding: '4px 8px'
  },
  removeButton: {
    backgroundColor: '#f48fb1',
    color: 'black'
  },
  addButton: {
    backgroundColor: '#4caf50',
    color: 'white'
  },
  empty: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '10vh',
    fontSize: '12px'
  }
})
